import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { getLscDetection, patchLscDetection } from '../../api';
import { lscPath } from '../../routing';
import type { LscDetectionPayload, LscDetectionSwitchValue } from '../../types';

const CHOICES: { value: LscDetectionSwitchValue; label: string }[] = [
  { value: 'unset', label: 'Default (on)' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

const PLAIN_REASONS: Record<string, string> = {
  kill_switch: 'Live game detection is paused by Stream Center.',
  cap_reached:
    "Live game detection is paused because this league reached today's detection cap.",
  deny_list: 'Live game detection is paused for this league.',
};

export interface DetectionSwitchControlProps {
  leagueId: string;
  canEdit: boolean;
  onChanged: () => void;
}

export const DetectionSwitchControl: React.FC<DetectionSwitchControlProps> = ({
  leagueId,
  canEdit,
  onChanged,
}) => {
  const [payload, setPayload] = useState<LscDetectionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canEdit) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLscDetection(leagueId)
      .then((next) => {
        if (!cancelled) {
          setPayload(next);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Live game detection could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canEdit, leagueId]);

  const choose = useCallback(
    async (value: LscDetectionSwitchValue) => {
      if (!canEdit || saving || payload?.detection.stored === value) return;
      setSaving(true);
      setError(null);
      try {
        const next = await patchLscDetection(leagueId, value);
        setPayload(next);
        onChanged();
      } catch {
        setError('Live game detection could not be saved.');
      } finally {
        setSaving(false);
      }
    },
    [canEdit, leagueId, onChanged, payload?.detection.stored, saving]
  );

  if (!canEdit) return null;

  if (loading) {
    return (
      <div className="card flex items-center gap-2 text-sm text-secondary-300" data-testid="detection-switch-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading live game detection
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="card text-sm text-red-200" data-testid="detection-switch-error">
        {error}
      </div>
    );
  }

  const reason = payload.detection.reason;
  const subscriptionHref = lscPath(`/leagues/${leagueId}/setup?step=subscription`);
  const importHref = lscPath(`/leagues/${leagueId}/import`);

  if (reason === 'entitlement_inactive') {
    return (
      <div className="card space-y-2" data-testid="detection-switch-entitlement">
        <h4 className="text-sm font-semibold text-white">Live game detection</h4>
        <p className="text-sm text-secondary-200">
          Live game detection is off until this league has an active subscription.
        </p>
        <Link to={subscriptionHref} className="btn-secondary text-sm inline-flex min-h-11 items-center px-4">
          View subscription
        </Link>
      </div>
    );
  }

  if (PLAIN_REASONS[reason]) {
    return (
      <div className="card space-y-2" data-testid="detection-switch-plain">
        <h4 className="text-sm font-semibold text-white">Live game detection</h4>
        <p className="text-sm text-secondary-200">{PLAIN_REASONS[reason]}</p>
      </div>
    );
  }

  return (
    <div className="card space-y-3" data-testid={`detection-switch-${payload.detection.stored}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">Live game detection</h4>
          <p className="text-xs text-secondary-400 mt-0.5">
            {payload.detection.effective
              ? 'Live game detection is on for this league.'
              : 'Live game detection is off.'}
          </p>
        </div>
        {saving ? <Loader2 className="h-4 w-4 animate-spin text-secondary-300" aria-hidden /> : null}
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Live game detection">
        {CHOICES.map((choice) => {
          const selected = payload.detection.stored === choice.value;
          return (
            <button
              key={choice.value}
              type="button"
              className={`text-sm min-h-11 px-4 rounded-md border ${
                selected
                  ? 'bg-white text-secondary-950 border-white'
                  : 'bg-transparent text-secondary-100 border-secondary-600'
              }`}
              aria-pressed={selected}
              disabled={saving}
              data-testid={`detection-switch-choice-${choice.value}`}
              onClick={() => void choose(choice.value)}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
      {payload.schedule_stale ? (
        <div
          className="rounded-lg border border-amber-700/40 bg-amber-950/10 px-4 py-3 text-sm text-amber-100 flex gap-2"
          data-testid="detection-switch-schedule-warning"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p>
            Your schedule is behind the league&apos;s current week.{' '}
            <Link to={importHref} className="underline">
              Sync your schedule
            </Link>
            .
          </p>
        </div>
      ) : null}
      {error ? <p className="text-sm text-red-200">{error}</p> : null}
    </div>
  );
};
